// Shared country/currency constants for PO / PE / Supplier forms.
// Bahrain is intentionally first so it's the default selection in dropdowns
// and so the VAT auto-rule (Bahrain → 10%, others → 0%) lands on it by default.

export const COUNTRIES = [
  // GCC + Middle East (most-used at the top for fast picking)
  'Bahrain', 'Saudi Arabia', 'United Arab Emirates', 'Kuwait', 'Qatar', 'Oman',
  'Egypt', 'Jordan', 'Lebanon', 'Iraq', 'Yemen', 'Iran', 'Turkey',
  // Asia
  'India', 'Pakistan', 'Bangladesh', 'Sri Lanka', 'Nepal',
  'China', 'Hong Kong', 'Taiwan', 'Japan', 'South Korea',
  'Singapore', 'Malaysia', 'Thailand', 'Vietnam', 'Indonesia', 'Philippines',
  // Europe
  'United Kingdom', 'Germany', 'France', 'Italy', 'Spain', 'Netherlands',
  'Belgium', 'Switzerland', 'Sweden', 'Norway', 'Denmark', 'Finland',
  'Poland', 'Czech Republic', 'Austria', 'Ireland', 'Portugal', 'Greece',
  'Russia', 'Ukraine',
  // Americas
  'United States', 'Canada', 'Mexico', 'Brazil', 'Argentina', 'Chile', 'Colombia',
  // Africa
  'South Africa', 'Nigeria', 'Kenya', 'Morocco', 'Tunisia', 'Algeria', 'Ethiopia',
  // Oceania
  'Australia', 'New Zealand',
];

// Common currencies. BHD first so it's the default for a Bahrain-based company.
export const CURRENCIES = [
  'BHD', 'SAR', 'AED', 'KWD', 'QAR', 'OMR',
  'USD', 'EUR', 'GBP',
  'INR', 'PKR', 'BDT', 'LKR', 'NPR',
  'CNY', 'HKD', 'TWD', 'JPY', 'KRW',
  'SGD', 'MYR', 'THB', 'IDR', 'PHP', 'VND',
  'CAD', 'AUD', 'NZD', 'CHF', 'SEK', 'NOK', 'DKK',
  'TRY', 'EGP', 'JOD', 'LBP', 'IQD', 'IRR', 'YER',
  'ZAR', 'NGN', 'KES', 'MAD',
  'RUB', 'UAH', 'PLN', 'CZK', 'BRL', 'ARS', 'CLP', 'COP', 'MXN',
];

// Default currency per country — used when the country dropdown changes to
// auto-suggest a currency. The user can still override.
export const COUNTRY_DEFAULT_CURRENCY = {
  'Bahrain': 'BHD',
  'Saudi Arabia': 'SAR',
  'United Arab Emirates': 'AED',
  'Kuwait': 'KWD',
  'Qatar': 'QAR',
  'Oman': 'OMR',
  'Egypt': 'EGP',
  'Jordan': 'JOD',
  'Lebanon': 'LBP',
  'Iraq': 'IQD',
  'Yemen': 'YER',
  'Iran': 'IRR',
  'Turkey': 'TRY',
  'India': 'INR',
  'Pakistan': 'PKR',
  'Bangladesh': 'BDT',
  'Sri Lanka': 'LKR',
  'Nepal': 'NPR',
  'China': 'CNY',
  'Hong Kong': 'HKD',
  'Taiwan': 'TWD',
  'Japan': 'JPY',
  'South Korea': 'KRW',
  'Singapore': 'SGD',
  'Malaysia': 'MYR',
  'Thailand': 'THB',
  'Vietnam': 'VND',
  'Indonesia': 'IDR',
  'Philippines': 'PHP',
  'United Kingdom': 'GBP',
  'Germany': 'EUR',
  'France': 'EUR',
  'Italy': 'EUR',
  'Spain': 'EUR',
  'Netherlands': 'EUR',
  'Belgium': 'EUR',
  'Switzerland': 'CHF',
  'Sweden': 'SEK',
  'Norway': 'NOK',
  'Denmark': 'DKK',
  'Finland': 'EUR',
  'Poland': 'PLN',
  'Czech Republic': 'CZK',
  'Austria': 'EUR',
  'Ireland': 'EUR',
  'Portugal': 'EUR',
  'Greece': 'EUR',
  'Russia': 'RUB',
  'Ukraine': 'UAH',
  'United States': 'USD',
  'Canada': 'CAD',
  'Mexico': 'MXN',
  'Brazil': 'BRL',
  'Argentina': 'ARS',
  'Chile': 'CLP',
  'Colombia': 'COP',
  'South Africa': 'ZAR',
  'Nigeria': 'NGN',
  'Kenya': 'KES',
  'Morocco': 'MAD',
  'Tunisia': 'TND',
  'Algeria': 'DZD',
  'Ethiopia': 'ETB',
  'Australia': 'AUD',
  'New Zealand': 'NZD',
};

// VAT rate per country — legacy table kept for backward compatibility.
// New code should NOT rely on this; multi-company white-label installs
// use each company's own `tax_percentage` setting and treat a supplier
// as "local" only when the supplier's country matches THAT company's
// country (see `isLocalCountry` + `resolveDefaultVat` below).
export const COUNTRY_VAT_RATE = {
  'Bahrain': 10,
};

export const getDefaultVat = (country) => COUNTRY_VAT_RATE[country] ?? 0;
export const getDefaultCurrency = (country) => COUNTRY_DEFAULT_CURRENCY[country] || 'BHD';

// Case-insensitive equality check used to decide if a supplier is "local"
// (same country as the company) vs international. Used by Purchase Orders
// & Purchase Entries to choose whether to apply VAT.
export const isLocalCountry = (supplierCountry, companyCountry) => {
  const a = (supplierCountry || '').trim().toLowerCase();
  const b = (companyCountry || '').trim().toLowerCase();
  return !!a && !!b && a === b;
};

// Pick the VAT % to default to for a given supplier country, scoped to
// the *current* company. Local supplier → company's configured tax %;
// international → 0. Falls back to the legacy Bahrain-=10 table only
// when company settings haven't loaded yet (avoids a flash of 0%).
export const resolveDefaultVat = (supplierCountry, company) => {
  const companyCountry = company?.country;
  const companyVat = parseFloat(company?.tax_percentage);
  if (isLocalCountry(supplierCountry, companyCountry)) {
    return Number.isFinite(companyVat) ? companyVat : getDefaultVat(supplierCountry);
  }
  return 0;
};
